import { lightDark, SeasonalTokenOverrides, SeasonConfig } from '../types';
import { DynamicBackground } from './dynamic-background';
import { FallingPetals } from './falling-petals';
import SpringLogoDark from './images/logo-dark.png';
import SpringLogoLight from './images/logo-light.png';
import springWreathSvg from './images/wreath.svg';
import SpringAmbientSound from './sounds/spring-ambiance.mp3';

/**
 * Spring Theme - Semantic Token Overrides
 *
 * Each token provides a { default (light), _dark } pair.
 * Light values are used during daytime (6 AM – 7 PM),
 * dark values during nighttime (7 PM – 6 AM).
 */
const springTokenOverrides: SeasonalTokenOverrides = {
	colors: {
		primary: {
			fg: lightDark('#059669', '#34d399'),
			fgStrong: lightDark('#047857', '#10b981'),
			fgStronger: lightDark('#065f46', '#059669'),
			fgStrongest: lightDark('#064e3b', '#047857'),
			bg: lightDark('#059669', '#10b981'),
			bgHover: lightDark('#047857', '#059669'),
			bgActive: lightDark('#065f46', '#047857'),
			bgAlpha: lightDark('rgba(5, 150, 105, 0.12)', 'rgba(52, 211, 153, 0.15)'),
			bgAlphaHover: lightDark('rgba(5, 150, 105, 0.20)', 'rgba(52, 211, 153, 0.25)'),
			bgAlphaActive: lightDark('rgba(5, 150, 105, 0.28)', 'rgba(52, 211, 153, 0.35)'),
		},
		bg: {
			canvas: lightDark('#faf9f6', 'transparent'),
			surface: lightDark('#ffffff', '#1e1b2e'),
			soft: lightDark('#f0fdf4', '#2a2640'),
			subtle: lightDark('#f5f3ef', '#231f38'),
			topNav: lightDark('#ffffff', '#16132a'),
			inset: lightDark('#ecfdf5', '#13112a'),
		},
		text: {
			interactive: lightDark('#059669', '#34d399'),
		},
		border: {
			default: lightDark('#d1d5db', '#3b3558'),
			soft: lightDark('#e5e7eb', '#2a2640'),
		},
	},
};

export const springConfig: SeasonConfig = {
	themeClassName: 'spring-theme',
	toggleEmoji: '🌸',

	colorModeStrategy: 'time-based',
	semanticTokenOverrides: springTokenOverrides,

	modalVariant: 'scenic',

	logoLight: SpringLogoLight,
	logoDark: SpringLogoDark,

	modalSound: SpringAmbientSound,
	modalSoundVolume: 0.1,

	buttonTexts: [
		'🌸 Bloom some praise',
		'🌱 Grow some gratitude',
		'🦋 Spread appreciation',
		'🌷 Plant some kudos',
		'☀️ Brighten their day',
		'🌻 Send sunshine',
		'🐝 Buzz with recognition',
		'🌈 Share the warmth',
	],

	modalDecorations: [
		{ emoji: '🌸', top: '24px', left: '24px', fontSize: '20px', opacity: 0.35, rotation: -18 },
		{ emoji: '🌷', top: '36px', right: '32px', fontSize: '18px', opacity: 0.3, rotation: 14 },
		{ emoji: '🌱', bottom: '130px', left: '28px', fontSize: '22px', opacity: 0.25, rotation: -8 },
		{ emoji: '🦋', bottom: '150px', right: '28px', fontSize: '20px', opacity: 0.28, rotation: 18 },
	],

	modalColors: {
		logoOverride: 'light',
		logoMaxWidth: '200px',
		overlayBg: 'blackAlpha.600',
		contentBg: '#fbfaf5',
		borderColor: 'rgba(16, 185, 129, 0.25)',
		boxShadow: '0 24px 60px -20px rgba(5, 150, 105, 0.35), 0 4px 12px rgba(0, 0, 0, 0.06)',
		logoDropShadowColor: 'transparent',
		subtitleColor: 'green.800',
		enableButtonBg: 'green.600',
		enableButtonHoverBg: 'green.500',
		enableButtonActiveBg: 'green.700',
		enableButtonBgGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
		enableButtonHoverBgGradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
		enableButtonBoxShadow: '0 6px 18px -4px rgba(5, 150, 105, 0.45)',
		enableButtonHoverBoxShadow: '0 12px 28px -6px rgba(5, 150, 105, 0.55)',
		enableButtonTextColor: 'white',
		cancelButtonColor: 'green.700',
		cancelButtonHoverColor: 'green.600',
		noticeBg: 'transparent',
		noticeBorderColor: 'transparent',
		noticeTextColor: 'green.700',
		featureCardBg: 'green.50',
		featureCardBorderColor: 'green.100',
		featureCardTextColor: 'gray.700',
		featureBubbleGradient: 'linear-gradient(135deg, #fdf2f8 0%, #ecfdf5 100%)',
		featureBubbleBorderColor: 'rgba(16, 185, 129, 0.18)',
		headerAccentGradient:
			'linear-gradient(180deg, rgba(186, 230, 253, 0.55) 0%, rgba(253, 230, 138, 0.3) 45%, rgba(236, 253, 245, 0.15) 80%, transparent 100%)',
		closeButtonHoverBg: 'blackAlpha.100',
	},

	modalText: {
		subtitle: 'Bring your Bonusly to life with our spring theme!',
		features: [
			{
				emoji: '🌗',
				text: 'Your landscape brightens and darkens with the day',
			},
			{
				emoji: '🌸',
				text: 'Flowers bloom as you and your team recognize each other',
			},
			{
				emoji: '🌈',
				text: 'Soft greens, warm petals, and a touch of sunshine',
			},
		],
		endDateNotice: 'This spring theme will be available from April 22nd to May 8th. Toggle it on/off anytime!',
		enableButtonText: 'Enable Spring Theme',
		cancelButtonText: 'Maybe Later',
	},

	EffectsComponent: () => (
		<>
			<DynamicBackground />
			<FallingPetals />
		</>
	),

	avatarOverlayImage: springWreathSvg,
	/** Slightly smaller + nudge down vs default 1.4 — spring wreath is taller (flowers) than winter */
	avatarOverlayScale: 1.28,
	avatarOverlayTranslateY: '3px',
};
